/**
 * Pure helpers for `generate_text` — default fields, fingerprint / stale,
 * submit payload, duplicate draft reset.
 */

import type { CreateTextGenerationRunPayload, TextGenerationRunState } from '../../../api'
import { buildProviderOptionNodeDefaultFields } from './capabilityProviderSelection'
import {
  GENERATE_TEXT_CONTENT_LENGTH_NEW_DEFAULT,
  GENERATE_TEXT_SKILL_VERSION_DEFAULT,
  inferDefaultGenerateTextSkillKey,
  normalizeGenerateTextFieldDefaults,
  type GenerateTextCurrentResult,
  type GenerateTextHistoryEntry,
  type GenerateTextInputAsset,
} from './generateTextContract'
import { resolveGenerateTextImageInputs } from './generateTextReadiness'
import type { InputMaterial } from './dataflow'
import { remapMentionEdgeIds } from './canvasRegenerateFork'
import { isVideoPromptEnhancementPurpose } from './videoPromptEnhancement'

export function buildGenerateTextNodeDefaultFields(args?: {
  locale?: string | null
  sourceNodeType?: string | null
  inputAssets?: GenerateTextInputAsset[]
}): Record<string, unknown> {
  const locale = args?.locale ?? null
  const defaults = normalizeGenerateTextFieldDefaults({}, locale)
  const sourceNodeType = args?.sourceNodeType ?? null
  return {
    inputAssets: Array.isArray(args?.inputAssets) ? args!.inputAssets : [],
    language: defaults.language,
    // VistoeraTASK-00594 — new nodes default to free length; do NOT use
    // normalize()'s MEDIUM fallback (that exists for legacy empty fields).
    contentLength: GENERATE_TEXT_CONTENT_LENGTH_NEW_DEFAULT,
    outputFormat: defaults.outputFormat,
    additionalInstruction: '',
    ...buildProviderOptionNodeDefaultFields(),
    skillKey: null,
    skillVersion: GENERATE_TEXT_SKILL_VERSION_DEFAULT,
    skillSelectionSource: 'AUTO',
    /** Soft hint for AUTO skill resolution (try-on vs generic). Not EXPLICIT. */
    preferredSkillKey: inferDefaultGenerateTextSkillKey(sourceNodeType),
    currentResult: null,
    generationHistory: [],
    runStatus: null,
    activeRunId: null,
    errorMessage: null,
    errorCode: null,
    needsRegeneration: false,
    collapsed: false,
    generatedFromFingerprint: null,
  }
}

export function computeGenerateTextInputFingerprint(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): string {
  const images = resolveGenerateTextImageInputs(args)
  const parts = images.map((img) =>
    [img.assetId || '', img.generatedOutputId || '', img.ready ? '1' : '0'].join(':'),
  )
  const textParts = (args.inputMaterials || [])
    .filter((m): m is Extract<InputMaterial, { kind: 'TEXT' }> => m.kind === 'TEXT')
    .map((m) => [m.nodeId, m.ready ? '1' : '0', String(m.text || '')].join(':'))
  const defaults = normalizeGenerateTextFieldDefaults(args.fields)
  const partsBase = [
    parts.join('|') || 'none',
    textParts.join('|') || 'notext',
    defaults.language,
    defaults.contentLength,
    defaults.outputFormat,
    defaults.additionalInstruction.trim(),
    String(args.fields.skillSelectionSource || 'AUTO'),
    String(args.fields.skillKey || ''),
    String(args.fields.preferredSkillKey || ''),
  ]
  if (isVideoPromptEnhancementPurpose(args.fields.purpose)) {
    const snap = args.fields.videoPromptContextSnapshot
    const snapObj = snap && typeof snap === 'object' ? (snap as Record<string, unknown>) : null
    partsBase.push(
      String(args.fields.purpose || ''),
      String(args.fields.boundVideoNodeId || ''),
      String(snapObj?.original_prompt || ''),
      String(snapObj?.source_video_node_id || ''),
    )
  }
  return partsBase.join('::')
}

export function isGenerateTextResultStale(args: {
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
}): boolean {
  const current = args.fields.currentResult as GenerateTextCurrentResult | null
  if (!current || !String(current.text || '').trim()) return false
  if (args.fields.needsRegeneration) return true
  const live = computeGenerateTextInputFingerprint(args)
  const stamped =
    (typeof current.inputFingerprint === 'string' && current.inputFingerprint) ||
    (typeof args.fields.generatedFromFingerprint === 'string' ? args.fields.generatedFromFingerprint : '')
  if (!stamped) return false
  return stamped !== live
}

export function syncInputAssetsFromMaterials(
  inputMaterials: InputMaterial[] | null | undefined,
): GenerateTextInputAsset[] {
  const out: GenerateTextInputAsset[] = []
  for (const m of inputMaterials || []) {
    if (m.kind === 'IMAGE') {
      const entityId = 'entityId' in m && m.entityId ? m.entityId : null
      const generatedOutputId = 'generatedOutputId' in m && m.generatedOutputId ? m.generatedOutputId : null
      const assetId = entityId || generatedOutputId || ''
      if (!assetId) continue
      out.push({
        assetId,
        assetType: 'image',
        sourceNodeId: m.nodeId,
        displayName: m.sourceLabel,
        role: 'image_ref',
        generatedOutputId,
      })
      continue
    }
    if (m.kind === 'TEXT') {
      // Canvas text materials are edge-derived: use source node id as the
      // stable reference and carry the live body in `context` for generation.
      // Never mint a duplicate Workspace text asset just to wire the edge.
      const text = typeof m.text === 'string' ? m.text : ''
      if (!text.trim()) continue
      out.push({
        assetId: m.nodeId,
        assetType: 'text',
        sourceNodeId: m.nodeId,
        displayName: m.sourceLabel,
        role: 'text_context',
        generatedOutputId: null,
        context: text,
      })
    }
  }
  return out
}

export function buildTextGenerationSubmitPayload(args: {
  canvasNodeId: string
  projectId: string
  fields: Record<string, unknown>
  inputMaterials?: InputMaterial[] | null
  skillKey: string
  skillVersion: string
  skillSelectionSource: 'AUTO' | 'EXPLICIT'
  providerSelectionMode: 'DEFAULT' | 'EXPLICIT'
  providerOptionId: string | null
  locale?: string | null
  scopedContext?: Record<string, unknown> | null
}): CreateTextGenerationRunPayload {
  const defaults = normalizeGenerateTextFieldDefaults(args.fields, args.locale)
  const images = resolveGenerateTextImageInputs({
    fields: args.fields,
    inputMaterials: args.inputMaterials,
  })
  const textAssets = syncInputAssetsFromMaterials(args.inputMaterials).filter((a) => a.assetType === 'text')
  const scopedContext =
    args.scopedContext ??
    (args.fields.videoPromptContextSnapshot && typeof args.fields.videoPromptContextSnapshot === 'object'
      ? (args.fields.videoPromptContextSnapshot as Record<string, unknown>)
      : null)
  const imageRows = images
    .map((img, index) => {
      const assetId = img.assetId || img.generatedOutputId
      if (!assetId) return null
      return {
        asset_id: assetId,
        asset_type: 'image',
        display_name: img.displayName ?? null,
        source_node_id: img.sourceNodeId ?? null,
        role: index === 0 ? 'primary' : 'reference',
      }
    })
    .filter((row): row is NonNullable<typeof row> => row != null)
  const textRows = textAssets.map((a, index) => ({
    asset_id: a.assetId,
    asset_type: 'text',
    display_name: a.displayName ?? null,
    source_node_id: a.sourceNodeId ?? null,
    role: index === 0 && imageRows.length === 0 ? 'primary' : 'text_context',
    context: a.context ?? null,
  }))
  return {
    canvas_node_id: args.canvasNodeId,
    project_id: args.projectId,
    input: {
      input_assets: [...imageRows, ...textRows],
      language: defaults.language,
      content_length: defaults.contentLength,
      output_format: defaults.outputFormat,
      additional_instruction: defaults.additionalInstruction.trim() || '',
      ...(scopedContext ? { scoped_context: scopedContext } : {}),
    },
    skill_selection_source: args.skillSelectionSource,
    skill_key: args.skillKey,
    skill_version: args.skillVersion,
    provider_selection_mode: args.providerSelectionMode,
    provider_option_id: args.providerOptionId,
  }
}

export function resultFromTextGenerationRun(
  run: TextGenerationRunState,
  inputFingerprint: string,
): GenerateTextCurrentResult | null {
  const text = typeof run.result_text === 'string' ? run.result_text : run.result?.text
  if (typeof text !== 'string' || !text.length) {
    // Allow empty string only when SUCCESS with explicit result object
    if (run.status !== 'SUCCESS' || !run.result) return null
  }
  const resolvedText = typeof text === 'string' ? text : String(run.result?.text ?? '')
  return {
    text: resolvedText,
    format: run.result?.format || run.output_format || 'plain',
    language: run.result?.language || run.language || 'auto',
    title: run.result?.title ?? null,
    resultId: run.result?.result_id ?? run.run_id,
    runId: run.run_id,
    skillKey: run.skill_key,
    skillVersion: run.skill_version,
    createdAt: run.completed_at || run.created_at,
    inputFingerprint,
  }
}

/** Canvas node fields `currentResult` + `runStatus` / `activeRunId` — Generate Text only. */
export function isGenerateTextRunBusy(runStatus: string | null | undefined): boolean {
  const s = String(runStatus || '').toUpperCase()
  return s === 'SUBMITTING' || s === 'QUEUED' || s === 'GENERATING'
}

/**
 * Current-generation presentation: while a run is active or after failure, never
 * show a prior success body in the result area (history keeps older rows).
 */
export function resolveGenerateTextCurrentPresentation(args: {
  currentResult: GenerateTextCurrentResult | null
  runStatus: string | null | undefined
}): GenerateTextCurrentResult | null {
  if (isGenerateTextRunBusy(args.runStatus)) return null
  if (String(args.runStatus || '').toUpperCase() === 'ERROR') return null
  return args.currentResult
}

/** Poll / applyRunState guard — late responses from superseded runs are ignored. */
export function shouldApplyGenerateTextRunUpdate(
  incomingRunId: string,
  activeRunId: string | null | undefined,
): boolean {
  const active = typeof activeRunId === 'string' ? activeRunId.trim() : ''
  if (!active) return true
  return incomingRunId === active
}

/**
 * When a new generation is accepted (submit passed validation), archive the
 * visible result to history and clear current-generation fields.
 */
export function buildGenerateTextNewRunPresentationPatch(args: {
  previousResult: GenerateTextCurrentResult | null
  generationHistory: GenerateTextHistoryEntry[]
}): {
  currentResult: null
  generationHistory: GenerateTextHistoryEntry[]
  needsRegeneration: false
  errorMessage: null
  errorCode: null
} {
  return {
    currentResult: null,
    generationHistory: pushGenerateTextHistory(args.previousResult, args.generationHistory),
    needsRegeneration: false,
    errorMessage: null,
    errorCode: null,
  }
}

export function pushGenerateTextHistory(
  previous: GenerateTextCurrentResult | null,
  history: GenerateTextHistoryEntry[],
): GenerateTextHistoryEntry[] {
  if (!previous || !String(previous.text || '').length) return history
  const nextVersion = (history[0]?.version || history.length || 0) + 1
  const entry: GenerateTextHistoryEntry = {
    result: previous,
    version: nextVersion,
    completedAt: previous.createdAt ?? null,
  }
  return [entry, ...history].slice(0, 20)
}

/** Duplicate = copy config as new draft; never copy execution / result. */
export function buildGenerateTextDuplicateFields(
  originFields: Record<string, unknown>,
  edgeIdMap: Map<string, string> = new Map(),
): Record<string, unknown> {
  const base = buildGenerateTextNodeDefaultFields({
    locale: typeof originFields.language === 'string' ? originFields.language : null,
    sourceNodeType: null,
    inputAssets: [],
  })
  const rawInstruction =
    typeof originFields.additionalInstruction === 'string' ? originFields.additionalInstruction : ''
  return {
    ...base,
    language: originFields.language ?? base.language,
    contentLength: originFields.contentLength ?? base.contentLength,
    outputFormat: originFields.outputFormat ?? base.outputFormat,
    additionalInstruction: remapMentionEdgeIds(rawInstruction, edgeIdMap),
    providerSelectionMode: originFields.providerSelectionMode ?? 'DEFAULT',
    providerOptionId: originFields.providerOptionId ?? null,
    providerDisplayName: originFields.providerDisplayName ?? null,
    skillSelectionSource: originFields.skillSelectionSource ?? 'AUTO',
    skillKey: originFields.skillKey ?? null,
    skillVersion: originFields.skillVersion ?? GENERATE_TEXT_SKILL_VERSION_DEFAULT,
    preferredSkillKey: originFields.preferredSkillKey ?? null,
    currentResult: null,
    generationHistory: [],
    runStatus: null,
    activeRunId: null,
    errorMessage: null,
    errorCode: null,
    needsRegeneration: false,
    generatedFromFingerprint: null,
  }
}

export function primarySourceNodeTypeFromMaterials(
  inputMaterials: InputMaterial[] | null | undefined,
): string | null {
  const first = (inputMaterials || []).find((m) => m.kind === 'IMAGE')
  return first?.nodeType ?? null
}
